/**
 * AI usage metering: ONE place that sees every paid model call.
 *
 * Importing this module (side effect) wraps globalThis.fetch once per
 * process/isolate. Every POST to OpenRouter (and BytePlus Ark) is observed:
 * model, tokens, real USD cost (`usage: { include: true }` is added to chat
 * bodies), OpenRouter generation id, status and error. One row per call goes
 * to the `aiUsage` table (convex/aiUsage.ts).
 *
 * Attribution (who / which feature) comes from `withAiUsage(...)`, set at the
 * entry points (Ads, agent loop, simulations, Sanity...). Calls made outside
 * any context are still logged, as feature "unattributed" with the X-Title
 * and caller hint, through the token-guarded /api/ai-usage/ingest route.
 *
 * Hard rule: logging never breaks or slows the AI call. Every metering step is
 * wrapped in try/catch; the response the caller gets is the provider's own
 * (streams are passed through a TransformStream that only watches bytes).
 *
 * No node: imports, so V8-runtime actions can import it too.
 */
import { openAIPinIfMissingFetch } from "./openRouterProviderPin";
import { makeFunctionReference } from "convex/server";

export type AiUsageFeature =
  | "ads_chat"
  | "ads_generate"
  | "ads_other"
  | "chat"
  | "agent_worker"
  | "agent_voice"
  | "simulation"
  | "sanity"
  | "generation"
  | "dm"
  | "call_recording"
  | "feed"
  | "assistance"
  | "memory"
  | "workflow"
  | "unattributed"
  | (string & {});

type MinimalCtx = {
  runMutation: (ref: any, args: any) => Promise<unknown>;
};

export type AiUsageStore = {
  ctx?: MinimalCtx;
  feature: AiUsageFeature;
  userId?: string;
  organizationId?: string;
  ref?: string;
  pending: Set<Promise<unknown>>;
};

export type AiUsageRow = {
  feature: string;
  userId?: string;
  organizationId?: string;
  ref?: string;
  provider: string;
  endpoint: string;
  model: string;
  keySource: "studio" | "byok" | "unknown";
  status: "ok" | "error";
  httpStatus?: number;
  error?: string;
  generationId?: string;
  promptTokens?: number;
  completionTokens?: number;
  cachedTokens?: number;
  reasoningTokens?: number;
  costUsd?: number;
  durationMs: number;
  streamed: boolean;
  source?: string;
  caller?: string;
  at: number;
};

const RECORD_REF = makeFunctionReference<"mutation">("aiUsage:record");
const INSTALLED = Symbol.for("yatishara.aiUsage.fetchInstalled");
const BASE_FETCH = Symbol.for("yatishara.aiUsage.baseFetch");

// ---------------------------------------------------------------------------
// Context: AsyncLocalStorage when the runtime has it (Node actions, worker);
// a simple stack otherwise (V8 actions run one request per isolate).
// ---------------------------------------------------------------------------
type Als = { getStore(): AiUsageStore | undefined; run<T>(s: AiUsageStore, fn: () => T): T };
let als: Als | null | undefined;
const fallbackStack: AiUsageStore[] = [];

function getAls(): Als | null {
  if (als !== undefined) return als;
  als = null;
  try {
    const p = (globalThis as { process?: { getBuiltinModule?: (id: string) => unknown } }).process;
    const mod = p?.getBuiltinModule?.("node:async_hooks") as
      | { AsyncLocalStorage?: new () => Als }
      | undefined;
    if (mod?.AsyncLocalStorage) als = new mod.AsyncLocalStorage();
  } catch {
    als = null;
  }
  return als;
}

export function currentAiUsage(): AiUsageStore | undefined {
  try {
    const a = getAls();
    if (a) return a.getStore();
  } catch {
    /* ignore */
  }
  return fallbackStack[fallbackStack.length - 1];
}

/** Run `fn` with AI usage attribution. Waits (bounded) for its log writes. */
export async function withAiUsage<T>(
  opts: {
    ctx?: MinimalCtx;
    feature: AiUsageFeature;
    userId?: string | null;
    organizationId?: string | null;
    ref?: string | null;
  },
  fn: () => Promise<T>,
): Promise<T> {
  const parent = currentAiUsage();
  const store: AiUsageStore = {
    ctx: opts.ctx ?? parent?.ctx,
    feature: opts.feature,
    userId: opts.userId ?? parent?.userId ?? undefined,
    organizationId: opts.organizationId ?? parent?.organizationId ?? undefined,
    ref: opts.ref ?? undefined,
    pending: new Set(),
  };
  const a = getAls();
  try {
    if (a) return await a.run(store, fn);
    fallbackStack.push(store);
    try {
      return await fn();
    } finally {
      const i = fallbackStack.lastIndexOf(store);
      if (i >= 0) fallbackStack.splice(i, 1);
    }
  } finally {
    await flushStore(store);
  }
}

/** Wrap a Convex handler: `handler: aiUsageHandler("ads_chat", async (ctx, args) => ...)`. */
export function aiUsageHandler<C extends MinimalCtx, A, R>(
  feature: AiUsageFeature,
  handler: (ctx: C, args: A) => Promise<R>,
): (ctx: C, args: A) => Promise<R> {
  return (ctx, args) => withAiUsage({ ctx, feature }, () => handler(ctx, args));
}

/** Add who/what to the current context once it is known (e.g. after loading the order). */
export function tagAiUsage(tags: {
  userId?: string | null;
  organizationId?: string | null;
  ref?: string | null;
  feature?: AiUsageFeature;
}): void {
  try {
    const s = currentAiUsage();
    if (!s) return;
    if (tags.userId) s.userId = String(tags.userId);
    if (tags.organizationId) s.organizationId = String(tags.organizationId);
    if (tags.ref) s.ref = String(tags.ref);
    if (tags.feature) s.feature = tags.feature;
  } catch {
    /* never throw */
  }
}

async function flushStore(store: AiUsageStore): Promise<void> {
  if (!store.pending.size) return;
  try {
    await Promise.race([
      Promise.allSettled([...store.pending]),
      new Promise((r) => setTimeout(r, 4000)),
    ]);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Parsing helpers (regex over head/tail text: works for JSON and SSE, and for
// huge image/video bodies without parsing them).
// ---------------------------------------------------------------------------
const HEAD_MAX = 16_384;
const TAIL_MAX = 65_536;

function lastNumber(text: string, key: string): number | undefined {
  const re = new RegExp(`(?<!\\\\)"${key}"\\s*:\\s*(-?[0-9]+(?:\\.[0-9]+)?(?:[eE][-+]?[0-9]+)?)`, "g");
  let m: RegExpExecArray | null;
  let out: number | undefined;
  while ((m = re.exec(text))) {
    const n = Number(m[1]);
    if (Number.isFinite(n)) out = n;
  }
  return out;
}

function firstString(text: string, key: string, prefix = ""): string | undefined {
  const re = new RegExp(`(?<!\\\\)"${key}"\\s*:\\s*"(${prefix}[^"\\\\]{0,200})"`);
  return re.exec(text)?.[1];
}

export function parseUsageText(head: string, tail: string) {
  const all = head.length + tail.length <= HEAD_MAX + TAIL_MAX ? head + "\n" + tail : tail;
  const cost = lastNumber(all, "cost") ?? lastNumber(all, "total_cost");
  const promptTokens = lastNumber(all, "prompt_tokens") ?? lastNumber(all, "input_tokens");
  const completionTokens = lastNumber(all, "completion_tokens") ?? lastNumber(all, "output_tokens");
  const cachedTokens = lastNumber(all, "cached_tokens");
  const reasoningTokens = lastNumber(all, "reasoning_tokens");
  const generationId =
    firstString(head, "id", "gen-") ?? firstString(all, "id", "gen-") ?? firstString(head, "id");
  const model = firstString(head, "model") ?? firstString(all, "model");
  return { cost, promptTokens, completionTokens, cachedTokens, reasoningTokens, generationId, model };
}

function urlOf(input: unknown): string {
  try {
    if (typeof input === "string") return input;
    if (input instanceof URL) return input.toString();
    return String((input as { url?: string })?.url ?? "");
  } catch {
    return "";
  }
}

function providerFor(url: string): string | null {
  if (url.includes("openrouter.ai/api/")) return "openrouter";
  if (/ark\.[a-z0-9-]+\.byteplus(api)?\.com|bytepluses\.com/i.test(url)) return "byteplus";
  if (url.includes("api.openai.com/")) return "openai";
  if (url.includes("api.anthropic.com/")) return "anthropic";
  if (url.includes("generativelanguage.googleapis.com/")) return "google";
  return null;
}

function headerValue(input: unknown, init: RequestInit | undefined, name: string): string {
  try {
    const lower = name.toLowerCase();
    const h = init?.headers ?? (input as Request)?.headers;
    if (!h) return "";
    if (typeof (h as Headers).get === "function") return (h as Headers).get(name) ?? "";
    if (Array.isArray(h)) {
      const hit = h.find(([k]) => String(k).toLowerCase() === lower);
      return hit ? String(hit[1]) : "";
    }
    for (const [k, val] of Object.entries(h as Record<string, string>)) {
      if (k.toLowerCase() === lower) return String(val ?? "");
    }
  } catch {
    /* ignore */
  }
  return "";
}

function keySourceFor(provider: string, auth: string): AiUsageRow["keySource"] {
  try {
    const token = auth.replace(/^Bearer\s+/i, "").trim();
    if (!token) return "unknown";
    const ours =
      provider === "openrouter"
        ? process.env.OPENROUTER_API_KEY
        : provider === "byteplus"
          ? process.env.BYTEPLUS_ARK_API_KEY ?? process.env.ARK_API_KEY
          : undefined;
    if (!ours) return "unknown";
    return token === ours.trim() ? "studio" : "byok";
  } catch {
    return "unknown";
  }
}

function callerHint(): string | undefined {
  try {
    const stack = new Error().stack ?? "";
    for (const line of stack.split("\n").slice(2)) {
      if (/aiUsage|node:internal|node_modules|providerCostCapture|<anonymous>/.test(line)) continue;
      const m = /([A-Za-z0-9_.\-/]+\.(?:m?js|ts))(?::\d+)?/.exec(line);
      if (m) return m[1]!.split("/").slice(-2).join("/").slice(0, 120);
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

function shouldMeter(method: string, url: string): boolean {
  if (method !== "POST") return false;
  // The generation-id lookup is a GET; listing/polling GETs are skipped too.
  return providerFor(url) !== null;
}

// ---------------------------------------------------------------------------
// Writing rows
// ---------------------------------------------------------------------------
async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Shared secret for /api/ai-usage/ingest, derived from the OpenRouter key (never sent raw). */
export async function aiUsageIngestToken(): Promise<string | null> {
  const key = process.env.OPENROUTER_API_KEY?.trim();
  if (!key) return null;
  return sha256Hex(`yatishara-ai-usage:${key}`);
}

function ingestUrl(): string | null {
  const base =
    process.env.CONVEX_SITE_URL?.trim() ||
    process.env.STUDIO_CONVEX_SITE_URL?.trim() ||
    process.env.AGENT_WORKER_SITE_URL?.trim() ||
    "";
  return base ? `${base.replace(/\/$/, "")}/api/ai-usage/ingest` : null;
}

async function writeRow(store: AiUsageStore | undefined, row: AiUsageRow): Promise<void> {
  try {
    if (store?.ctx) {
      await store.ctx.runMutation(RECORD_REF, { row });
      return;
    }
    const url = ingestUrl();
    const token = await aiUsageIngestToken();
    if (!url || !token) {
      console.warn("[aiUsage] no ctx and no ingest route; row dropped", { model: row.model });
      return;
    }
    const base = ((globalThis as Record<symbol, unknown>)[BASE_FETCH] as typeof fetch) ?? fetch;
    await base(url, {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      body: JSON.stringify({ row }),
    });
  } catch (error) {
    console.warn("[aiUsage] log failed (AI call unaffected)", String((error as Error)?.message ?? error).slice(0, 200));
  }
}

function track(store: AiUsageStore | undefined, p: Promise<unknown>) {
  if (!store) return;
  store.pending.add(p);
  p.finally(() => store.pending.delete(p)).catch(() => {});
}

// ---------------------------------------------------------------------------
// The fetch wrapper
// ---------------------------------------------------------------------------
export function meteredFetch(baseFetch: typeof fetch): typeof fetch {
  const wrapped = (async (input: RequestInfo | URL, init?: RequestInit) => {
    let url = "";
    let method = "GET";
    try {
      url = urlOf(input);
      method = String(init?.method ?? (input as Request)?.method ?? "GET").toUpperCase();
    } catch {
      return baseFetch(input as RequestInfo, init);
    }
    if (!shouldMeter(method, url)) return baseFetch(input as RequestInfo, init);

    const started = Date.now();
    const store = currentAiUsage();
    let nextInit = init;
    let model = "";
    let streamed = false;
    const provider = providerFor(url) ?? "unknown";
    try {
      if (typeof init?.body === "string" && init.body.length < 20_000_000) {
        const parsed = JSON.parse(init.body) as Record<string, unknown>;
        model = typeof parsed.model === "string" ? parsed.model : "";
        streamed = parsed.stream === true;
        if (
          provider === "openrouter" &&
          /\/(chat\/completions|completions|responses)(\?|$)/.test(url) &&
          parsed.usage == null
        ) {
          parsed.usage = { include: true };
          nextInit = { ...init, body: JSON.stringify(parsed) };
        }
      }
    } catch {
      nextInit = init;
    }
    let endpoint = "";
    try {
      endpoint = new URL(url).pathname.replace(/^\/api\//, "").slice(0, 80);
    } catch {
      endpoint = url.slice(0, 80);
    }
    const base = (): Omit<AiUsageRow, "status" | "durationMs"> => ({
      feature: store?.feature ?? "unattributed",
      ...(store?.userId ? { userId: store.userId } : {}),
      ...(store?.organizationId ? { organizationId: store.organizationId } : {}),
      ...(store?.ref ? { ref: store.ref.slice(0, 120) } : {}),
      provider,
      endpoint,
      model: model || "unknown",
      keySource: keySourceFor(provider, headerValue(input, init, "authorization")),
      streamed,
      source: headerValue(input, init, "x-title").slice(0, 120) || undefined,
      caller: store ? undefined : callerHint(),
      at: started,
    });

    let res: Response;
    try {
      res = await baseFetch(input as RequestInfo, nextInit);
    } catch (error) {
      try {
        const row: AiUsageRow = {
          ...base(),
          status: "error",
          error: String((error as Error)?.message ?? error).slice(0, 500),
          durationMs: Date.now() - started,
        };
        track(store, writeRow(store, row));
      } catch {
        /* ignore */
      }
      throw error;
    }

    try {
      if (!res.body) {
        track(
          store,
          writeRow(store, {
            ...base(),
            status: res.ok ? "ok" : "error",
            httpStatus: res.status,
            durationMs: Date.now() - started,
          }),
        );
        return res;
      }
      const decoder = new TextDecoder();
      let head = "";
      let tail = "";
      let total = 0;
      let done = false;
      const finish = (aborted: string | null) => {
        if (done) return;
        done = true;
        try {
          const u = parseUsageText(head, tail);
          const errText = !res.ok ? (head + tail).slice(0, 500) : aborted;
          const row: AiUsageRow = {
            ...base(),
            model: model || u.model || "unknown",
            status: res.ok && !aborted ? "ok" : "error",
            httpStatus: res.status,
            ...(errText ? { error: errText } : {}),
            ...(u.generationId ? { generationId: u.generationId } : {}),
            ...(u.promptTokens !== undefined ? { promptTokens: u.promptTokens } : {}),
            ...(u.completionTokens !== undefined ? { completionTokens: u.completionTokens } : {}),
            ...(u.cachedTokens !== undefined ? { cachedTokens: u.cachedTokens } : {}),
            ...(u.reasoningTokens !== undefined ? { reasoningTokens: u.reasoningTokens } : {}),
            ...(u.cost !== undefined && u.cost >= 0 ? { costUsd: u.cost } : {}),
            durationMs: Date.now() - started,
          };
          track(store, writeRow(store, row));
        } catch {
          /* ignore */
        }
      };
      const watch = new TransformStream<Uint8Array, Uint8Array>({
        transform(chunk, controller) {
          controller.enqueue(chunk);
          try {
            const text = decoder.decode(chunk, { stream: true });
            total += text.length;
            if (head.length < HEAD_MAX) head += text.slice(0, HEAD_MAX - head.length);
            tail = (tail + text).slice(-TAIL_MAX);
          } catch {
            /* ignore */
          }
        },
        flush() {
          finish(null);
        },
      });
      const body = res.body.pipeThrough(watch);
      // If the consumer cancels mid-stream we still log what we saw.
      const out = new Response(body, {
        status: res.status,
        statusText: res.statusText,
        headers: res.headers,
      });
      try {
        Object.defineProperty(out, "url", { value: res.url });
      } catch {
        /* ignore */
      }
      void total;
      return out;
    } catch {
      return res;
    }
  }) as typeof fetch;
  return wrapped;
}

function install() {
  try {
    const g = globalThis as Record<symbol, unknown> & { fetch: typeof fetch };
    if (g[INSTALLED] || typeof g.fetch !== "function") return;
    const original = g.fetch.bind(globalThis);
    g[BASE_FETCH] = original;
    g.fetch = meteredFetch(openAIPinIfMissingFetch(original));
    g[INSTALLED] = true;
  } catch (error) {
    console.warn("[aiUsage] could not install fetch meter", String(error));
  }
}

install();
